import React, { useState, useEffect } from 'react';

// Validation libraries
import { useFormik } from 'formik';
import * as yup from 'yup';

// Hooks
import { useDispatch } from 'react-redux';

// Actions
import { createContact, updateSenderProfile } from '~/redux/actions/contacts-actions';
import { success, failure } from '~/redux/actions/snackbar-actions';

// Utils
import { MESSAGES } from '~/utils/message';
import { handlePhoneNumberChange } from '~/utils/helperFunctions';
import { validateForm, realTimeValidation, resetValidation } from '~/utils/formik-validation';
import { EMAIL_REGEX } from '~/utils/constants';
import { CircularProgress } from '@mui/material';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Grid,
  Avatar,
  InputAdornment,
  Divider,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { senderAddress, senderUser } from '~/assets/images';

// Styles
import './SenderProfileModal.scss';
import OlcButton from '~/components/General/OlcButton';
import OlcFormModal from '~/components/General/OlcFormModal';

const SenderProfileModal = ({
  openModal,
  handleCloseModal,
  selectedProfile,
  mode = 'add',
  onSuccess,
}) => {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Validation schema
  const validationSchema = yup.object().shape({
    profileName: yup
      .string()
      .trim()
      .required('Profile name is required')
      .max(30, 'Profile name must be less than 30 characters'),
    firstName: yup
      .string()
      .trim()
      .test({
        test: function (value) {
          const { companyName } = this.parent;
          const trimmedValue = value?.trim();
          const trimmedCompanyName = companyName?.trim();
          return !!trimmedValue || !!trimmedCompanyName;
        },
        message: 'First name or company name is required',
      })
      .max(30, 'First name must be less than 30 characters')
      .notRequired(),
    companyName: yup
      .string()
      .trim()
      .max(100, 'Company name must be less than 100 characters')
      .test({
        test: function (value) {
          const { firstName } = this.parent;
          const trimmedValue = value?.trim();
          const trimmedFirstName = firstName?.trim();
          return !!trimmedValue || !!trimmedFirstName;
        },
        message: 'First name or company name is required',
      })
      .notRequired(),
    lastName: yup.string().trim().max(30, 'Last name must be less than 30 characters'),
    companyWebsite: yup
      .string()
      .trim()
      .max(200, 'Website URL must be less than 200 characters')
      .test('is-url', 'Invalid website URL format', function (value) {
        const trimmedValue = value?.trim();
        if (!trimmedValue) return true; // Allow empty value (not required)
        // URL validation regex - supports http, https, and domains without protocol
        const urlPattern =
          /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/;
        return urlPattern.test(trimmedValue);
      }),
    phoneNo: yup
      .string()
      .trim()
      .max(20, 'Phone number must be less than 20 characters')
      .nullable(true),
    email: yup.string().trim().matches(EMAIL_REGEX, 'Invalid email address'),
    address1: yup
      .string()
      .trim()
      .max(100, 'Address must be less than 100 characters')
      .required('Mailing Address 1 is required'),
    address2: yup.string().trim().max(100, 'Address must be less than 100 characters'),
    city: yup
      .string()
      .trim()
      .max(40, 'City must be less than 40 characters')
      .required('Mailing City is required'),
    state: yup
      .string()
      .trim()
      .max(40, 'State must be less than 40 characters')
      .required('Mailing State is required'),
    zip: yup
      .string()
      .trim()
      .max(10, 'Zip code must be less than 10 characters')
      .required('Mailing Zip Code is required'),
  });

  const formik = useFormik({
    initialValues: {
      profileName: '',
      firstName: '',
      lastName: '',
      companyName: '',
      companyWebsite: '',
      phoneNo: '',
      email: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      zip: '',
    },
    validationSchema: validationSchema,
    validateOnChange: hasSubmitted, // After first submit, validate on change
    validateOnBlur: hasSubmitted, // After first submit, validate on blur
  });

  useEffect(() => {
    // Reset validation state when modal opens or mode changes
    setHasSubmitted(false);

    if (selectedProfile && mode === 'edit') {
      // Populate form with selected profile data
      const newFormData = {
        profileName: selectedProfile.profileName || '',
        firstName: selectedProfile.firstName || '',
        lastName: selectedProfile.lastName || '',
        companyName: selectedProfile.companyName || '',
        companyWebsite: selectedProfile.websiteUrl || '',
        phoneNo: selectedProfile.phoneNo || '',
        email: selectedProfile.email || '',
        address1: selectedProfile.address1 || '',
        address2: selectedProfile.address2 || '',
        city: selectedProfile.city || '',
        state: selectedProfile.state || '',
        zip: selectedProfile.zip || '',
      };

      formik.setValues(newFormData);
    } else {
      // Reset form for add mode
      formik.resetForm();
      resetValidation();
    }
  }, [selectedProfile, mode, openModal]);

  const handleChange = (field) => (event) => {
    const newValue = event.target.value;

    // Handle phone number formatting
    if (field === 'phoneNo') {
      handlePhoneNumberChange(
        newValue,
        (fieldName, value) => {
          formik.setFieldValue(fieldName, value);
          // Don't mark as touched on change - wait for blur
        },
        field
      );
      return; // handlePhoneNumberChange handles the state update
    }

    formik.setFieldValue(field, newValue);
    // Don't validate on every keystroke - only validate on blur
  };

  const handleClearField = (field) => {
    formik.setFieldValue(field, '');
    // Mark as touched so validation shows on blur
    formik.setFieldTouched(field, true);
  };

  const handleSave = async () => {
    // Mark as submitted to enable real-time validation
    setHasSubmitted(true);

    // Mark all fields as touched to show validation errors
    const fieldNames = Object.keys(formik.initialValues);
    fieldNames.forEach((fieldName) => {
      formik.setFieldTouched(fieldName, true);
    });

    // Comprehensive validation to prevent API calls with invalid data
    // Step 1: Validate form using formik - wait for validation to complete
    const validationErrors = await formik.validateForm();

    // Step 2: Check if there are any validation errors
    const hasErrors =
      Object.keys(validationErrors).length > 0 || Object.keys(formik.errors).length > 0;
    if (hasErrors) {
      console.log('Validation errors found:', validationErrors || formik.errors);
      return; // Don't proceed with API call if there are validation errors
    }

    // Step 3: Additional validation check using the utility function
    const isValid = validateForm(formik);
    if (!isValid) {
      console.log('Form validation failed');
      return;
    }

    // Step 4: Final check to ensure form is valid before API call
    if (!formik.isValid) {
      console.log('Form is not valid according to formik');
      return;
    }

    try {
      setLoading(true);

      // Map form fields to API field names
      const payload = {
        profileName: formik.values.profileName,
        firstName: formik.values.firstName,
        lastName: formik.values.lastName,
        companyName: formik.values.companyName,
        websiteUrl: formik.values.companyWebsite,
        phoneNo: formik.values.phoneNo,
        email: formik.values.email,
        address1: formik.values.address1,
        address2: formik.values.address2,
        city: formik.values.city,
        state: formik.values.state,
        zip: formik.values.zip,
        isReturnAddress: true, // Always set isReturnAddress to true for sender profiles
      };

      // Clean up empty fields - remove undefined/empty values but keep isReturnAddress
      Object.keys(payload).forEach((key) => {
        if (!payload[key] && key !== 'isReturnAddress') {
          delete payload[key];
        }
      });

      let response;
      if (mode === 'edit') {
        payload.id = selectedProfile.id;
        response = await dispatch(updateSenderProfile(payload));
      } else {
        response = await dispatch(createContact(payload));
      }

      if (response.status === 200) {
        dispatch(success(response.data.message || MESSAGES.CONTACTS.CREATE_SUCCESS));
        setHasSubmitted(false); // Reset validation state on success
        handleCloseModal();

        if (onSuccess) {
          onSuccess(response?.data?.data);
        }
      } else {
        dispatch(
          failure(
            response?.data?.data?.errors[0]?.msg ||
              response?.data?.message ||
              MESSAGES.GENERAL_ERROR
          )
        );
      }
    } catch (error) {
      dispatch(failure(error?.message || MESSAGES.GENERAL_ERROR));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    formik.resetForm();
    resetValidation();
    setHasSubmitted(false); // Reset validation state
    handleCloseModal();
  };

  // Generate avatar initials
  const getInitials = () => {
    const first = formik.values.firstName?.[0] || '';
    const last = formik.values.lastName?.[0] || '';
    return `${first}${last}`.toUpperCase();
  };

  return (
    <OlcFormModal
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          handleClose();
        }
      }}
      open={openModal}
      title={mode === 'edit' ? 'Edit Sender Profile' : 'Add New Sender Profile'}
      className="senderProfileModalWrapper"
      footer={
        <>
          <OlcButton variant="secondary" onClick={handleClose} disabled={loading}>
            Cancel
          </OlcButton>
          <OlcButton variant="primary" onClick={handleSave} disabled={loading}>
            {loading ? (
              <CircularProgress
                sx={{
                  color: 'white',
                  width: '20px !important',
                  height: '20px !important',
                }}
              />
            ) : mode === 'edit' ? (
              'Save'
            ) : (
              'Create'
            )}
          </OlcButton>
        </>
      }
    >
      <Box className="senderProfileModalContent">
        <Box className="formContainer ">
          <Box className="formSection">
            <Typography className="fieldLabel">
              Sender Profile Name<span className="olcRequired">*</span>
            </Typography>
            <TextField
              fullWidth
              name="profileName"
              placeholder="Enter Friendly Name"
              value={formik.values.profileName}
              onChange={handleChange('profileName')}
              onBlur={formik.handleBlur}
              variant="outlined"
              className="spLargeField"
              autoComplete="off"
              error={!!(formik.touched.profileName && formik.errors.profileName)}
              helperText={formik.touched.profileName && formik.errors.profileName}
              inputProps={{
                autoComplete: 'new-password',
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ width: '16px', marginRight: '8px' }}>
                    {formik.values.profileName && (
                      <IconButton
                        size="small"
                        onClick={() => handleClearField('profileName')}
                        edge="end"
                        sx={{ padding: '4px' }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Sender Information Section */}
          <Box className="formSection" style={{ marginTop: '4px' }}>
            <Box className="formSectionWrapper">
              <Box className="sectionHeader">
                <Box className="sectionIcon">
                  <img src={senderUser} alt="senderUser" />
                </Box>
                <Typography className="sectionTitle">Sender Information</Typography>
              </Box>

              <Box className="smallFieldsMainWrapper">
                <Box className="smallFieldsWrapper">
                  <Box className="formSection">
                    <Typography className="fieldLabel">First Name</Typography>
                    <TextField
                      fullWidth
                      name="firstName"
                      placeholder="Enter First Name"
                      value={formik.values.firstName}
                      onChange={handleChange('firstName')}
                      onBlur={formik.handleBlur}
                      variant="outlined"
                      className="spSmallField"
                      size="small"
                      autoComplete="off"
                      error={!!(formik.touched.firstName && formik.errors.firstName)}
                      helperText={formik.touched.firstName && formik.errors.firstName}
                      inputProps={{
                        autoComplete: 'new-password',
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end" sx={{ width: '16px', marginRight: '8px' }}>
                            {formik.values.firstName && (
                              <IconButton
                                size="small"
                                onClick={() => handleClearField('firstName')}
                                edge="end"
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            )}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                  <Box className="formSection">
                    <Typography className="fieldLabel">Last Name</Typography>
                    <TextField
                      fullWidth
                      name="lastName"
                      placeholder="Enter Last Name"
                      value={formik.values.lastName}
                      onChange={handleChange('lastName')}
                      onBlur={formik.handleBlur}
                      variant="outlined"
                      size="small"
                      className="spSmallField"
                      autoComplete="off"
                      error={!!(formik.touched.lastName && formik.errors.lastName)}
                      helperText={formik.touched.lastName && formik.errors.lastName}
                      inputProps={{
                        autoComplete: 'new-password',
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end" sx={{ width: '16px', marginRight: '8px' }}>
                            {formik.values.lastName && (
                              <IconButton
                                size="small"
                                onClick={() => handleClearField('lastName')}
                                edge="end"
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            )}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </Box>

                <Box className="smallFieldsWrapper">
                  <Box className="formSection">
                    <Typography className="fieldLabel">Phone Number</Typography>
                    <TextField
                      fullWidth
                      name="phoneNo"
                      placeholder="(123) 456-7890"
                      value={formik.values.phoneNo}
                      onChange={handleChange('phoneNo')}
                      onBlur={formik.handleBlur}
                      variant="outlined"
                      className="spSmallField"
                      size="small"
                      autoComplete="off"
                      error={!!(formik.touched.phoneNo && formik.errors.phoneNo)}
                      helperText={formik.touched.phoneNo && formik.errors.phoneNo}
                      inputProps={{
                        autoComplete: 'new-password',
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end" sx={{ width: '16px', marginRight: '8px' }}>
                            {formik.values.phoneNo && (
                              <IconButton
                                size="small"
                                onClick={() => handleClearField('phoneNo')}
                                edge="end"
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            )}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  <Box className="formSection">
                    <Typography className="fieldLabel">Email Address</Typography>
                    <TextField
                      fullWidth
                      name="email"
                      placeholder="Enter Email Address"
                      value={formik.values.email}
                      onChange={handleChange('email')}
                      onBlur={formik.handleBlur}
                      variant="outlined"
                      className="spSmallField"
                      size="small"
                      type="email"
                      autoComplete="off"
                      error={!!(formik.touched.email && formik.errors.email)}
                      helperText={formik.touched.email && formik.errors.email}
                      inputProps={{
                        autoComplete: 'new-password',
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end" sx={{ width: '16px', marginRight: '8px' }}>
                            {formik.values.email && (
                              <IconButton
                                size="small"
                                onClick={() => handleClearField('email')}
                                edge="end"
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            )}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </Box>

                <Box className="smallFieldsWrapper">
                  <Box className="formSection">
                    <Typography className="fieldLabel">Company Name</Typography>
                    <TextField
                      fullWidth
                      name="companyName"
                      placeholder="Enter Company Name"
                      value={formik.values.companyName}
                      onChange={handleChange('companyName')}
                      onBlur={formik.handleBlur}
                      variant="outlined"
                      className="spSmallField"
                      size="small"
                      autoComplete="off"
                      error={!!(formik.touched.companyName && formik.errors.companyName)}
                      helperText={formik.touched.companyName && formik.errors.companyName}
                      inputProps={{
                        autoComplete: 'new-password',
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end" sx={{ width: '16px', marginRight: '8px' }}>
                            {formik.values.companyName && (
                              <IconButton
                                size="small"
                                onClick={() => handleClearField('companyName')}
                                edge="end"
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            )}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  <Box className="formSection">
                    <Typography className="fieldLabel">Company Website</Typography>
                    <TextField
                      fullWidth
                      name="companyWebsite"
                      placeholder="Enter Company Website"
                      value={formik.values.companyWebsite}
                      onChange={handleChange('companyWebsite')}
                      onBlur={formik.handleBlur}
                      variant="outlined"
                      className="spSmallField"
                      size="small"
                      autoComplete="off"
                      error={!!(formik.touched.companyWebsite && formik.errors.companyWebsite)}
                      helperText={formik.touched.companyWebsite && formik.errors.companyWebsite}
                      inputProps={{
                        autoComplete: 'new-password',
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end" sx={{ width: '16px', marginRight: '8px' }}>
                            {formik.values.companyWebsite && (
                              <IconButton
                                size="small"
                                onClick={() => handleClearField('companyWebsite')}
                                edge="end"
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            )}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              width: '100%',
              height: '.5px',
              backgroundColor: 'rgba(0, 0, 0, 0.24)',
            }}
          />
          {/* Sender Return Address Section */}

          <Box className="formSection">
            <Box className="formSectionWrapper">
              <Box className="sectionHeader">
                <Box className="sectionIcon">
                  <img src={senderAddress} alt="senderAddress" />
                </Box>
                <Typography className="sectionTitle">Sender Return Address</Typography>
              </Box>

              <Box className="smallFieldsMainWrapper">
                <Box className="smallFieldsWrapper">
                  <Box className="formSection">
                    <Typography className="fieldLabel">
                      Mailing Address 1<span className="olcRequired">*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      name="address1"
                      placeholder="Enter Mailing Address 1*"
                      value={formik.values.address1}
                      onChange={handleChange('address1')}
                      onBlur={formik.handleBlur}
                      variant="outlined"
                      size="small"
                      className="spSmallField"
                      autoComplete="off"
                      error={!!(formik.touched.address1 && formik.errors.address1)}
                      helperText={formik.touched.address1 && formik.errors.address1}
                      inputProps={{
                        autoComplete: 'new-password',
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end" sx={{ width: '16px', marginRight: '8px' }}>
                            {formik.values.address1 && (
                              <IconButton
                                size="small"
                                onClick={() => handleClearField('address1')}
                                edge="end"
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            )}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  <Box className="formSection">
                    <Typography className="fieldLabel">Mailing Address 2</Typography>
                    <TextField
                      fullWidth
                      name="address2"
                      placeholder="Enter Mailing Address 2"
                      value={formik.values.address2}
                      onChange={handleChange('address2')}
                      onBlur={formik.handleBlur}
                      variant="outlined"
                      className="spSmallField"
                      size="small"
                      autoComplete="off"
                      error={!!(formik.touched.address2 && formik.errors.address2)}
                      helperText={formik.touched.address2 && formik.errors.address2}
                      inputProps={{
                        autoComplete: 'new-password',
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end" sx={{ width: '16px', marginRight: '8px' }}>
                            {formik.values.address2 && (
                              <IconButton
                                size="small"
                                onClick={() => handleClearField('address2')}
                                edge="end"
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            )}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </Box>

                <Box className="smallFieldsWrapper">
                  <Box className="formSection">
                    <Typography className="fieldLabel">
                      Mailing City<span className="olcRequired">*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      name="city"
                      placeholder="Enter Mailing City*"
                      value={formik.values.city}
                      onChange={handleChange('city')}
                      onBlur={formik.handleBlur}
                      variant="outlined"
                      size="small"
                      className="spSmallField"
                      autoComplete="off"
                      error={!!(formik.touched.city && formik.errors.city)}
                      helperText={formik.touched.city && formik.errors.city}
                      inputProps={{
                        autoComplete: 'new-password',
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end" sx={{ width: '16px', marginRight: '8px' }}>
                            {formik.values.city && (
                              <IconButton
                                size="small"
                                onClick={() => handleClearField('city')}
                                edge="end"
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            )}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  <Box className="formSection">
                    <Typography className="fieldLabel">
                      Mailing State<span className="olcRequired">*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      name="state"
                      placeholder="Enter Mailing State*"
                      value={formik.values.state}
                      onChange={handleChange('state')}
                      onBlur={formik.handleBlur}
                      variant="outlined"
                      size="small"
                      className="spSmallField"
                      autoComplete="off"
                      error={!!(formik.touched.state && formik.errors.state)}
                      helperText={formik.touched.state && formik.errors.state}
                      inputProps={{
                        autoComplete: 'new-password',
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end" sx={{ width: '16px', marginRight: '8px' }}>
                            {formik.values.state && (
                              <IconButton
                                size="small"
                                onClick={() => handleClearField('state')}
                                edge="end"
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            )}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </Box>

                <Box className="smallFieldsWrapper">
                  <Box className="formSection">
                    <Typography className="fieldLabel">
                      Mailing Zip Code<span className="olcRequired">*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      name="zip"
                      placeholder="Enter Mailing Zip Code*"
                      value={formik.values.zip}
                      onChange={handleChange('zip')}
                      onBlur={formik.handleBlur}
                      variant="outlined"
                      className="spSmallField"
                      size="small"
                      autoComplete="off"
                      error={!!(formik.touched.zip && formik.errors.zip)}
                      helperText={formik.touched.zip && formik.errors.zip}
                      inputProps={{
                        autoComplete: 'new-password',
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end" sx={{ width: '16px', marginRight: '8px' }}>
                            {formik.values.zip && (
                              <IconButton
                                size="small"
                                onClick={() => handleClearField('zip')}
                                edge="end"
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            )}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Action Buttons - Outside scrollable content */}
      </Box>
    </OlcFormModal>
  );
};

export default SenderProfileModal;
